import React from 'react';
import _ from 'lodash';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { useMutation, useParameterizedQuery } from 'react-fetching-library';
import { Box, Button, Tabs, Tab, Badge } from '@mui/material';
import { updateEmployeeApplication, createQuestionnaireAction } from '../../../../services/request';
import { useApplication } from '../ApplicationsContext';
import defaultImage from './default.png';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import AttachmentIcon from '@mui/icons-material/Attachment';
import CalendarViewMonthIcon from '@mui/icons-material/CalendarViewMonth';
import EmployeeData from './tabs/EmployeeData';
import Settlement from './tabs/Settlement';
import Attachments from './tabs/Attachments';
import Documents from './tabs/Documents';
import Notifications from './tabs/Notifications';
import History from './tabs/History';
import { Periods } from './tabs/Periods';
import Iconify from '../../../../components/Iconify';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { useDidMountEffect } from '../../../../services/hooks';
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';

const validationSchema = Yup.object({
  name: Yup.string().required('To pole nie może być puste'),
  surname: Yup.string().required('To pole nie może być puste'),
  correspondence_email: Yup.string().email('Proszę podać poprawny adres e-mail')
});

export default function ApplicationForm({ data }) {
  const { rowData, setRowData, state, rows, setRows } = useApplication();
  const { seasonIdentifier } = useSelector((state => state.app));
  const { loading, mutate } = useMutation(updateEmployeeApplication);
  const { query } = useParameterizedQuery(createQuestionnaireAction);
  const [currentTab, setCurrentTab] = React.useState(0);
  const ref = React.useRef(null);
  const [preview, setPreview] = React.useState(_.isNil(rowData.photo) ? defaultImage : rowData.photo);
  const [isDefaultPhoto, setisDefaultPhoto] = React.useState(rowData.is_default_photo);

  const formik = useFormik({
    initialValues: {
      id: rowData.id,
      employee_type: !_.isNil(rowData.employee_type) ? rowData.employee_type : null,
      name: !_.isNil(rowData.name) ? rowData.name : '',
      surname: !_.isNil(rowData.surname) ? rowData.surname : '',
      birth_date: !_.isNil(rowData.birth_date) ? rowData.birth_date : null,
      correspondence_email: !_.isNil(rowData.correspondence_email) ? rowData.correspondence_email : '',
      phone: !_.isNil(rowData.phone) ? rowData.phone : '',
      place_of_birth: !_.isNil(rowData.place_of_birth) ? rowData.place_of_birth : '',
      pesel: !_.isNil(rowData.pesel) ? rowData.pesel : '',
      company_name: !_.isNil(rowData.company_name) ? rowData.company_name : '',
      address: {
        city: !_.isNil(rowData.address) && !_.isNil(rowData.address.city) ? rowData.address.city : '',
        house: !_.isNil(rowData.address) && !_.isNil(rowData.address.house) ? rowData.address.house : '',
        postal_code: !_.isNil(rowData.address) && !_.isNil(rowData.address.postal_code) ? rowData.address.postal_code : '',
        street: !_.isNil(rowData.address) && !_.isNil(rowData.address.street) ? rowData.address.street : ''
      },
      account_number: !_.isNil(rowData.account_number) ? rowData.account_number : '',
      nip: !_.isNil(rowData.nip) ? rowData.nip : '',
      function_type: !_.isNil(rowData.function_type) ? rowData.function_type : '',
      education: !_.isNil(rowData.education) ? rowData.education : '',
      current_employee_status: !_.isNil(rowData.current_employee_status) ? rowData.current_employee_status : '',
      experience: !_.isNil(rowData.experience) ? rowData.experience : '',
      preferred_age_group: !_.isNil(rowData.preferred_age_group) ? rowData.preferred_age_group : '',
      preferred_age_group_multiple: !_.isNil(rowData.preferred_age_group_multiple) ? rowData.preferred_age_group_multiple : [],
      company_clothing_size: !_.isNil(rowData.company_clothing_size) ? rowData.company_clothing_size : '',
      source_of_offer: !_.isNil(rowData.source_of_offer) ? rowData.source_of_offer : '',
      additional_comments: !_.isNil(rowData.additional_comments) ? rowData.additional_comments : '',
      about_me: !_.isNil(rowData.about_me) ? rowData.about_me : '',
      other_pedagogical_qualifications: !_.isNil(rowData.other_pedagogical_qualifications) ? rowData.other_pedagogical_qualifications : '',
      other_additional_qualifications: !_.isNil(rowData.other_additional_qualifications) ? rowData.other_additional_qualifications : '',
      other_skills: !_.isNil(rowData.other_skills) ? rowData.other_skills : '',
      recommended_by: !_.isNil(rowData.recommended_by) ? rowData.recommended_by : '',
      what_experience: !_.isNil(rowData.what_experience) ? rowData.what_experience : '',
      where_experience: !_.isNil(rowData.where_experience) ? rowData.where_experience : '',
      role_experience: !_.isNil(rowData.role_experience) ? rowData.role_experience : '',
      pedagogical_qualifications: !_.isNil(rowData.pedagogical_qualifications) ? rowData.pedagogical_qualifications : [],
      additional_qualifications: !_.isNil(rowData.additional_qualifications) ? rowData.additional_qualifications : [],
      range_of_skills: !_.isNil(rowData.range_of_skills) ? rowData.range_of_skills : [],
      periods: !_.isNil(rowData.periods) ? rowData.periods : [],
      zs_periods: !_.isNil(rowData.zs_periods) ? rowData.zs_periods : [],
      timed_periods: !_.isNil(rowData.timed_periods) ? rowData.timed_periods : [],
      zs_timed_periods: !_.isNil(rowData.zs_timed_periods) ? rowData.zs_timed_periods : [],
      is_photo_deleted: false,
      is_photo_changed: false,
      agreement_number: !_.isNil(rowData.agreement_number) ? rowData.agreement_number : '',
      application_annexes: !_.isNil(rowData.application_annexes) ? rowData.application_annexes : [],
      state: !_.isNil(rowData.state) ? rowData.state : '',
      supplements: !_.isNil(rowData.supplements) ? rowData.supplements : null,
      supplements_info: !_.isNil(rowData.supplements_info) ? rowData.supplements_info : null,
      daily_rate: !_.isNil(rowData.daily_rate) ? rowData.daily_rate : null,
      hourly_rate_winter: !_.isNil(rowData.hourly_rate_winter) ? rowData.hourly_rate_winter : null,
      hourly_rate_summer: !_.isNil(rowData.hourly_rate_summer) ? rowData.hourly_rate_summer : null,
      hourly_rate: !_.isNil(rowData.hourly_rate) ? rowData.hourly_rate : null,
      hours_worked: !_.isNil(rowData.hours_worked) ? rowData.hours_worked : null,
      to_pay: !_.isNil(rowData.to_pay) ? rowData.to_pay : null,
      to_pay_net_or_gross: !_.isNil(rowData.to_pay_net_or_gross) ? rowData.to_pay_net_or_gross : null,
      checkbox_sections: !_.isNil(rowData.checkbox_sections) ? rowData.checkbox_sections : [],
      notifications: !_.isNil(rowData.notifications) ? rowData.notifications : [],
      manual_notifications: !_.isNil(rowData.manual_notifications) ? rowData.manual_notifications : false,
      automatic_notifications: !_.isNil(rowData.automatic_notifications) ? rowData.automatic_notifications : false,
      periods_type: !_.isNil(rowData.periods_type) ? rowData.periods_type : null,
      agreement_date_from: !_.isNil(rowData.agreement_date_from) ? rowData.agreement_date_from : null,
      agreement_date_to: !_.isNil(rowData.agreement_date_to) ? rowData.agreement_date_to : null,
      own_transport: !_.isNil(rowData.own_transport) ? rowData.own_transport : false,
      transport_has_to_be_picked_up: !_.isNil(rowData.transport_has_to_be_picked_up) ? rowData.transport_has_to_be_picked_up : false,
      transport_info: !_.isNil(rowData.transport_info) ? rowData.transport_info : '',
      diet: !_.isNil(rowData.diet) ? rowData.diet : '',
      works_in_additional_periods: !_.isNil(rowData.works_in_additional_periods) ? rowData.works_in_additional_periods : null,
      register_checked: !_.isNil(rowData.register_checked) ? rowData.register_checked : false,
      ol_periods: !_.isNil(rowData.ol_periods) ? rowData.ol_periods : [],
      ol_timed_periods: !_.isNil(rowData.ol_timed_periods) ? rowData.ol_timed_periods : [],
      date: !_.isNil(rowData.date) ? rowData.date : null,
      instructor_sport_type: !_.isNil(rowData.instructor_sport_type) ? rowData.instructor_sport_type : null,
      resort_for_agreement: !_.isNil(rowData.resort_for_agreement) ? rowData.resort_for_agreement : null,
      instructor_specialization: !_.isNil(rowData.instructor_specialization) ? rowData.instructor_specialization : null,
      changed_periods: !_.isNil(rowData.changed_periods) ? rowData.changed_periods : null,
      annex_paragraph1_text: !_.isNil(rowData.annex_paragraph1_text) ? rowData.annex_paragraph1_text : '',
      payment_records: !_.isNil(rowData.payment_records) ? rowData.payment_records : [],
      bonus_payment_records: !_.isNil(rowData.bonus_payment_records) ? rowData.bonus_payment_records : [],
      settlements_notes: !_.isNil(rowData.settlements_notes) ? rowData.settlements_notes : '',
      salary_change_info: !_.isNil(rowData.salary_change_info) ? rowData.salary_change_info : null,
      new_document_added_by_staff: !_.isNil(rowData.new_document_added_by_staff) ? rowData.new_document_added_by_staff : false,
      notes: !_.isNil(rowData.notes) ? rowData.notes : '',
      consents: !_.isNil(rowData.consents) ? rowData.consents : []
    },
    validationSchema,
    validateOnChange: false,
    enableReinitialize: true,
    onSubmit: values => {
      const formData = new FormData();
      formData.append('data', JSON.stringify(values));
      if (!_.isNil(ref.current) && !_.isNil(ref.current.files[0])) {
        formData.append('img', ref.current.files[0]);
      }
      handleSubmit(rowData.id, formData);
    }
  });

  useDidMountEffect(() => {
    if (state !== formik.values.state) {
      (async () => {
        const { payload, error, loading: qLoading } = await mutate(rowData.id, { state });
        if (!error && !qLoading) {
          setRowData(payload);
          const cloneRows = _.cloneDeep(rows);
          const idx = cloneRows.findIndex(item => item.id == payload.id);
          cloneRows[idx] = payload;
          setRows(cloneRows);
        } else if (_.isArray(payload)) {
          toast.error(payload[0]);
        } else {
          toast.error('Nie udało się zmienić statusu');
        }
      })();
    }
  }, [state]);


  const handlePhotoChange = deleteImage => {
    if (deleteImage) {
      formik.setFieldValue('is_photo_deleted', true);
      setPreview(defaultImage);
    } else {
      formik.setFieldValue('is_photo_deleted', false);
      setPreview(_.isNil(rowData.photo) ? defaultImage : rowData.photo);
    }

    formik.setFieldValue('is_photo_changed', false);

    if (ref.current !== null) {
      ref.current.value = null;
    }
  };

  const handleChangeImage = e => {
    const image = e.target.files[0];
    if (['image/jpg', 'image/jpeg', 'image/png'].includes(image.type)) {
      const r = new FileReader();
      r.onload = e => {
        formik.setFieldValue('is_photo_changed', true);
        setPreview(e.target.result);
      };
      r.readAsDataURL(image);
    } else {
      handlePhotoChange(false);
    }
  };
  const handleSubmit = async (applicationId, values) => {
    const { error, payload } = await mutate(applicationId, values);
    if (!error) {
      formik.resetForm({
        values: { ...payload, is_photo_deleted: false, is_photo_changed: false, ol_timed_periods: values.ol_timed_periods }
      });
      setRowData(payload);
      setisDefaultPhoto(payload.is_default_photo);
      const cloneRows = _.cloneDeep(rows);
      const idx = cloneRows.findIndex(item => item.id == payload.id);
      cloneRows[idx] = payload;
      setRows(cloneRows);
    } else {
      // eslint-disable-next-line no-lonely-if
      if (_.isArray(payload)) {
        toast.error(payload[0]);
      } else {
        toast.error('Nie udało się zapisać zmian');
      }
    }
  };

  const createQuestionnaire = async id => {
    const { error, loading, payload } = await query(id);

    if (!error && !loading) {
      setRowData(prev => ({ ...prev, questionnaire_state: 'sent' }));
    }

    if (error) {
      toast.error('Nie udało się wysłać kwestionariusza');
    }
  };

  return (
    <form onSubmit={formik.handleSubmit}>
      <Tabs sx={{ mb: 2 }} value={currentTab} onChange={(e, value) => setCurrentTab(value)}>
        <Tab icon={<AssignmentIndIcon />} label="Dane pracownika" />
        <Tab icon={<CalendarViewMonthIcon />} label="Terminy" />
        <Tab icon={<Iconify icon={'mdi:currency-usd'} width={20} height={20} />} label="rozliczenia" />
        <Tab icon={<AttachmentIcon />} label="Załączniki" />
        <Tab icon={<Iconify icon={'mdi:file-document-multiple'} width={20} height={20} />} label="Dokumenty" />
        <Tab icon={<NotificationsIcon />} label="Powiadomienia" />
        <Tab icon={<Iconify icon={'mdi:clipboard-text-clock'} width={20} height={20} />} label="Historia zmian" />
      </Tabs>
      <Box style={{ textAlign: 'center' }}>
        <Button variant="contained" disabled={!formik.dirty || loading || seasonIdentifier == 'l_23_rekrutacja'} fullWidth type="submit">
          {loading ? 'Zapisywanie...' : 'Zapisz zmiany'}
        </Button>
      </Box>
      <Box sx={{ p: 1, mt: 2, height: '65vh', overflow: 'auto' }}>
        {currentTab === 0
          ? <EmployeeData
            formik={formik}
            fileInputRef={ref}
            createQuestionnaire={createQuestionnaire}
            handleChangeImage={handleChangeImage}
            handlePhotoChange={handlePhotoChange}
            loading={loading}
            preview={preview}
            isDefaultPhoto={isDefaultPhoto}
          /> : currentTab === 1
            ? <Periods
              formik={formik}
            /> : currentTab === 2
              ? <Settlement
                formik={formik}
              /> : currentTab === 3
                ? <Attachments
                  formik={formik}
                /> : currentTab === 4
                  ? <Documents
                    formik={formik}
                  /> : currentTab === 5
                    ? <Notifications
                      formik={formik}
                    /> : currentTab === 6
                      ? <History
                        formik={formik}
                      /> : null}
      </Box>
    </form>
  );
};
